import { useState, useEffect, useRef } from "react";
import { DatePicker, Spin, Empty } from "antd";
import {
  CheckCircleOutlined,
  EnvironmentOutlined,
  HistoryOutlined,
  FieldTimeOutlined,
  HourglassOutlined,
} from "@ant-design/icons";
import { Bar, Chart } from "react-chartjs-2";
import dayjs from "dayjs";
import SummaryCard from "./SummaryCard";
import ChartCard from "./ChartCard";
import { minutesToDisplay } from "../utils/timeUtils";
import {
  disabledDate,
  roundCount,
  roundPercent,
  TOOLTIPS,
  resetChartLegend,
  buildDeliveryCountData,
  buildDeliveryCountOptions,
  buildDeviceTimeData,
  buildDeviceTimeOptions,
  LEGEND_DELIVERY_COUNT,
  LEGEND_INTEGRATED_CYCLE,
  LEGEND_INTEGRATED_DRIVE,
  LEGEND_INTEGRATED_WAIT,
} from "../utils/dashboardUtils";
import {
  getIntegratedSummary,
  getIntegratedDevicesCharts,
} from "../services/dashboardApi";

const { RangePicker } = DatePicker;

const DEFAULT_RANGE = [dayjs().subtract(30, "day"), dayjs().subtract(1, "day")];

const EMPTY_DESC = (
  <span>
    현재 조건에 해당하는 데이터가 없어
    <br />
    데이터가 표시되지 않습니다.
  </span>
);

const EmptyChart = () => (
  <div
    style={{
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EMPTY_DESC} />
  </div>
);

const DashboardIntegrated = () => {
  const [dateRange, setDateRange] = useState(DEFAULT_RANGE);
  const [summary, setSummary] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(false);

  const deliveryRef = useRef(null);
  const cycleRef = useRef(null);
  const driveRef = useRef(null);
  const waitRef = useRef(null);

  useEffect(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return;
    const params = {
      startYmd: dateRange[0].format("YYYY-MM-DD"),
      endYmd: dateRange[1].format("YYYY-MM-DD"),
    };
    setLoading(true);
    Promise.all([
      getIntegratedSummary(params),
      getIntegratedDevicesCharts(params),
    ])
      .then(([s, c]) => {
        setSummary(s);
        setCharts(c);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [dateRange]);

  return (
    <div className="dashboard-tab-content">
      {/* 조회 기간 */}
      <div className="dashboard-filter-row">
        <div className="dashboard-filter-date">
          <label>조회 기간</label>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            format="YYYY.MM.DD"
            disabledDate={disabledDate}
            placeholder={["YYYY.MM.DD", "YYYY.MM.DD"]}
          />
          <p className="filter-note">
            조회 기간은 현재 기준 6개월 이내 데이터 중 최대 30일 범위로 설정할
            수 있습니다.
          </p>
        </div>
      </div>

      <Spin spinning={loading}>
        <div className="spin-content">
          {/* 요약 */}
          <div className="kpi-section">
            <SummaryCard
              icon={
                <CheckCircleOutlined
                  style={{ color: "rgba(0,0,0,0.45)", fontSize: 28 }}
                />
              }
              label="유효 배송 횟수"
              tooltip={TOOLTIPS.validDelivery}
              mainValue={
                summary?.validDeliveryCount == null ? (
                  "-"
                ) : (
                  <>
                    <span>
                      {roundCount(summary?.validDeliveryCount).toFixed(2)}
                    </span>
                    <span style={{ fontSize: 18 }}>회</span>
                  </>
                )
              }
              subText={
                summary?.totalDeliveryCount != null
                  ? `총 ${summary.totalDeliveryCount}회 배송 시도`
                  : null
              }
            />
            <SummaryCard
              icon={
                <EnvironmentOutlined
                  style={{ color: "rgba(0,0,0,0.45)", fontSize: 28 }}
                />
              }
              label="배송 목적지 도달률"
              tooltip={TOOLTIPS.arrivalRate}
              mainValue={
                roundPercent(summary?.deliveryArrivalRate) == null ? (
                  "-"
                ) : (
                  <>
                    <span>{roundPercent(summary.deliveryArrivalRate)}</span>
                    <span style={{ fontSize: 18 }}>%</span>
                  </>
                )
              }
            />
            <SummaryCard
              icon={
                <HistoryOutlined
                  style={{ color: "rgba(0,0,0,0.45)", fontSize: 28 }}
                />
              }
              label="배송 시간"
              tooltip={TOOLTIPS.cycleTimeIntegrated}
              mainValue={minutesToDisplay(summary?.totalCycleDurationMinutes)}
            />
            <SummaryCard
              icon={
                <FieldTimeOutlined
                  style={{ color: "rgba(0,0,0,0.45)", fontSize: 28 }}
                />
              }
              label="운행 시간"
              tooltip={TOOLTIPS.driveTime}
              mainValue={minutesToDisplay(summary?.totalDriveMinutes)}
            />
            <SummaryCard
              icon={
                <HourglassOutlined
                  style={{ color: "rgba(0,0,0,0.45)", fontSize: 28 }}
                />
              }
              label="대기 시간"
              tooltip={TOOLTIPS.waitTime}
              mainValue={minutesToDisplay(summary?.totalWaitMinutes)}
            />
          </div>

          {/* 디바이스별 지표 */}
          <div className="dashboard-charts-wrapper">
            {/* 배송 횟수 - 전체 너비 */}
            <ChartCard
              title="배송 횟수"
              chartRef={deliveryRef}
              onReset={() => resetChartLegend(deliveryRef)}
              legendItems={LEGEND_DELIVERY_COUNT}
            >
              <div className="chart-container" style={{ height: 240 }}>
                {charts &&
                  (charts.deliveryChart.labels.length === 0 ? (
                    <EmptyChart />
                  ) : (
                    <Chart
                      ref={deliveryRef}
                      type="bar"
                      data={buildDeliveryCountData(
                        charts.deliveryChart.labels,
                        charts.deliveryChart.attemptCounts,
                        charts.deliveryChart.validCounts,
                        charts.deliveryChart.arrivalRates,
                      )}
                      options={buildDeliveryCountOptions()}
                    />
                  ))}
              </div>
            </ChartCard>

            {/* 배송 시간 / 운행 시간 / 대기 시간 - 3열 */}
            <div className="charts-row-3">
              <ChartCard
                title="배송 시간"
                chartRef={cycleRef}
                onReset={() => resetChartLegend(cycleRef)}
                legendItems={LEGEND_INTEGRATED_CYCLE}
              >
                <div className="chart-container" style={{ height: 200 }}>
                  {charts &&
                    (charts.cycleDurationChart.labels.length === 0 ? (
                      <EmptyChart />
                    ) : (
                      <Bar
                        ref={cycleRef}
                        data={buildDeviceTimeData(
                          charts.cycleDurationChart.labels,
                          charts.cycleDurationChart.totals,
                          charts.cycleDurationChart.avgs,
                          "#009DAD",
                          "#D6D4CC",
                        )}
                        options={buildDeviceTimeOptions()}
                      />
                    ))}
                </div>
              </ChartCard>

              <ChartCard
                title="운행 시간"
                chartRef={driveRef}
                onReset={() => resetChartLegend(driveRef)}
                legendItems={LEGEND_INTEGRATED_DRIVE}
              >
                <div className="chart-container" style={{ height: 200 }}>
                  {charts &&
                    (charts.driveChart.labels.length === 0 ? (
                      <EmptyChart />
                    ) : (
                      <Bar
                        ref={driveRef}
                        data={buildDeviceTimeData(
                          charts.driveChart.labels,
                          charts.driveChart.totals,
                          charts.driveChart.avgs,
                          "#003D48",
                          "#D6D4CC",
                        )}
                        options={buildDeviceTimeOptions()}
                      />
                    ))}
                </div>
              </ChartCard>

              <ChartCard
                title="대기 시간"
                chartRef={waitRef}
                onReset={() => resetChartLegend(waitRef)}
                legendItems={LEGEND_INTEGRATED_WAIT}
              >
                <div className="chart-container" style={{ height: 200 }}>
                  {charts &&
                    (charts.waitTimeChart.labels.length === 0 ? (
                      <EmptyChart />
                    ) : (
                      <Bar
                        ref={waitRef}
                        data={buildDeviceTimeData(
                          charts.waitTimeChart.labels,
                          charts.waitTimeChart.totals,
                          charts.waitTimeChart.avgs,
                          "#007F8C",
                          "#D6D4CC",
                        )}
                        options={buildDeviceTimeOptions()}
                      />
                    ))}
                </div>
              </ChartCard>
            </div>
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default DashboardIntegrated;
